import { useEffect, useRef, useState } from "react";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import CTLTable from "application/components/CTLTable";
import { getColumns, Attributes, operationURLS } from "./model";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";
import { application } from "globals/endpoints";
import CTLBaseDialogForm from "application/components/CTLBaseDialogForm";
import MDSnackbar from "components/MDSnackbar";
import DeleteConfirmation from "application/components/DeleteConfirmation";
import CTLNotification from "application/components/CTLNotification";
import { CircularProgress, Fade } from "@mui/material";
import { makeStyles } from "@material-ui/core";
import MDTypography from "components/MDTypography";
import { telemetry } from "globals/endpoints";
import UploadFileDialog from "./UploadFileDialog";
import UploadSampleFileDialog from "./UploadSampleFileDialog";
import FullConfigDialog from "./ConfigDialog";
import loggerFactory from "globals/logger/logger-factory";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: theme.zIndex.modal + 1,
  },
}));

function Asset({ onLoad, pageId, searchText, registerCallBacks, pageName }) {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage } = controller;
  const classes = useStyles();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmMultipleDelete, setConfirmMultipleDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [updateList, setUpdateList] = useState(Date.now());
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [deviceData, setDeviceData] = useState();
  const [metadata, setMetaData] = useState();
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);
  const [openMetaFrom, setOpenMetaForm] = useState(false);
  const [getAssetId, setAssetId] = useState("");
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const [openUploadSampleFile, setOpenUploadSampleFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingSampleFile, setUploadingSampleFile] = useState(false);
  const [rowData, setRowData] = useState("");
  const [openFullConfigDialog, setOpenFullConfigDialog] = useState(false);
  const selectedRows = useRef([])
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  useEffect(() => {
    if (Attributes) {
      const isViewOnly = AuthenticationService.isViewOnlyUser();
      onLoad({
        ...Attributes,
        enableAddButton: Attributes.enableAddButton && !isViewOnly,
        enableMultipleDeleteButton:
          Attributes.enableMultipleDeleteButton && !isViewOnly,
        enableUploadFaxButton: Attributes.enableUploadFaxButton && !isViewOnly,
        enableUploadButton: Attributes.enableUploadButton && !isViewOnly,
      });
    }
    registerCallBacks &&
      registerCallBacks({
        openAdvancedFilter: onOpenFilter,
        openForm: onOpenForm,
        onUploadFax: onUploadFax,
        onUpload: onUploadSampleFile,
        onMultipleDelete: onMultipleDelete,
      });
  }, [activePage]);

  function onOpenFilter() {
    setOpenFilter(true);
  }

  function onOpenForm() {
    setFormData({
      eventTime: Date.now(),
    });
    setOpenForm(true);
  }

  function onCloseFilter() {
    setOpenFilter(false);
  }

  function onApplyFilter(advancedFilters) {
    setAdvancedFilters(advancedFilters);
    setOpenFilter(false);
  }

  const onSelectionChange = (ids) => {
    const selected = Array.isArray(ids) ? ids : [ids];
    selectedRows.current = selected;
  };

  const editRow = (row) => {
    setFormData(row);
    setOpenForm(true);
  };

  const deleteRow = (row) => {
    setSelectedRow(row);
    setConfirmDelete(true);
  };

  const onCancel = () => {
    setSelectedRow([]);
    setConfirmDelete(false);
    setConfirmMultipleDelete(false);
  };

  const onUploadFax = () => {
    setOpenUploadFile(true);
  };

  const onUploadSampleFile = () => {
    setOpenUploadSampleFile(true);
  };

  const handleUploadSampleFile = async (selectedFile) => {
    try {
      setUploadingSampleFile(true);

      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await telemetry.post(
        "deviceManager/upload/sample-file",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      setNotify({
        isOpen: true,
        message: "",
        type: "success",
        pagename: pageName,
        status: response?.data?.message || "File uploaded successfully",
      });
    } catch (error) {
      setNotify({
        isOpen: true,
        message: "Upload Failed",
        type: "error",
        pagename: pageName,
        status: error?.response?.data?.message || "Failed to upload sample file",
      });
    } finally {
      setUploadingSampleFile(false);
      setOpenUploadSampleFile(false);
    }
  };

  const handleUploadFile = async (selectedFile) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("peer", "file");
      formData.append("file", selectedFile);

      const response = await telemetry.post(
        "SCADAAsset/uploadCSVStatus",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const resp = response.data;

      // Correct API condition
      if (resp.status !== 0) {
        setNotify({
          isOpen: true,
          message: resp.message,
          type: "error",
          pagename: pageName,
          status: "Upload Failed",
        });
        setUpdateList(Date.now());
        setUploading(false);
        setOpenUploadFile(false);
        return;
      }

      const successRows = resp.data.successReport?.rows || [];
      const errorRows = resp.data.errorReport?.rows || [];

      // Build success section
      const successSection = [
        "[SUCCESS REPORT]",
        "Device-Type,NodeName,IP,MAC-Address,Category,Status",
        ...successRows.map(
          (r) => `${r.deviceType},${r.nodeName},${r.ip},${r.mac},${r.category},SUCCESS`,
        ),
      ].join("\n");

      // Build error section
      const errorSection = [
        "",
        "[ERROR REPORT]",
        "Device-Type,NodeName,IP,MAC-Address,Category,Reason",
        ...errorRows.map((r) => `${r.deviceType},${r.nodeName},${r.ip},${r.mac},${r.category},${r.reason}`),
      ].join("\n");

      const finalCSV = successSection + "\n\n" + errorSection;

      // Download CSV
      const blob = new Blob([finalCSV], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "Upload_CSV_Report.csv";
      link.click();

      setNotify({
        isOpen: true,
        type: "success",
        pagename: pageName,
        message: resp.message,
        status: `Total Success: ${resp.data.successReport.totalSuccess},
Total Failed: ${resp.data.errorReport.totalFailed}`,
      });
      setUpdateList(Date.now());
      setUploading(false);
      setOpenUploadFile(false);
    } catch (error) {
      setUploading(false);
      setOpenUploadFile(false);
      setUpdateList(Date.now());
      setNotify({
        isOpen: true,
        message: "Upload Failed",
        type: "error",
        pagename: pageName,
        status: error?.response?.data?.message || "API Error",
      });
    }
  };

  const onMultipleDelete = () => {
    if (selectedRows.current.length != 0) {
      setConfirmMultipleDelete(true);
    } else {
      setNotify({
        isOpen: true,
        message: "Please select at least one row to delete.",
        type: "warning",
        pagename: pageName,
        status: "No rows selected for deletion.",
      });
    }
  };

  const onMultipleDeleteAgree = () => {
    deleteAsset(selectedRows.current);
  };

  const onAgree = (row) => {
    deleteAsset([row.id]);
  };

  const deleteAsset = (id) => {
    application
      .post(operationURLS.delete, {
        id: id,
      })
      .then((response) => {
        setUpdateList(Date.now());
        setNotify({
          isOpen: true,
          message: "",
          type: "success",
          pagename: pageName,
          status: response.data.messageDetail,
        });
      });
    selectedRows.current = [];
    setSelectedRow([]);
    setConfirmDelete(false);
    setConfirmMultipleDelete(false);
  };

  function onSaveForm(data) {
    if (typeof data.ringPorts === "string") {
      data = { ...data, ringPorts: data.ringPorts.trim() };
    }
    setSaving(true);
    if (data.id) {
      application
        .post(operationURLS.update, data)
        .then((response) => {
          setUpdateList(Date.now());
           setNotify({
             isOpen: true,
             message: "",
             type: "success",
             pagename: pageName,
             status: response.data.messageDetail,
           });
          setSaving(false);
          setOpenForm(false);
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error?.response?.data?.message || "Error",
            type: "error",
            pagename: pageName,
            status:
              error?.response?.data?.messageDetail ||
              "Something went wrong. Please try again.",
          });
          setSaving(false);
        });
    } else {
      application
        .post(operationURLS.create, data)
        .then((response) => {
          setUpdateList(Date.now());
           setNotify({
             isOpen: true,
             message: "",
             type: "success",
             pagename: pageName,
             status: response.data.messageDetail,
           });
          setSaving(false);
          setOpenForm(false);
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error?.response?.data?.message || "Error",
            type: "error",
            pagename: pageName,
            status:
              error?.response?.data?.messageDetail ||
              "Something went wrong. Please try again.",
          });
          setSaving(false);
        });
    }
  }

  function onCloseForm() {
    setOpenForm(false);
  }

  function onCloseMetaForm() {
    setOpenMetaForm(false);
  }

  function onSaveMetaForm(data) {
    application
      .post(`SCADAAsset/create/${getAssetId}`, data)
      .then((response) => {
        setOpenMetaForm(false);
        setNotify({
          isOpen: true,
          message: response.data.status,
          type: "success",
          pagename: pageName,
          status: response.data.message,
        });
      })
      .catch((error) => {
        setOpenMetaForm(false);
        setNotify({
          isOpen: true,
          message: error.response.message,
          type: "error",
          pagename: pageName,
          status: error.response.messageDetail,
        });
      });
  }

  const onUpdateConfiguration = (row) => {
    setOpenFullConfigDialog(true);
    setRowData(row);
  };

  const onUpdateConfigurationSubmit = async (configData) => {
    console.log("Configuration Data Submitted: ", configData);
    setLoading(true);

    if (!configData?.macAddress) {
      setNotify({
        isOpen: true,
        message: "",
        type: "warning",
        status: "MAC Address not found!",
      });

      setLoading(false);
      return;
    }

    try {
      const response = await telemetry.post(
        `deviceManager/update/configuration/${configData.macAddress}`,
        configData
      );
      setNotify({
        isOpen: true,
        message: response.data?.status || "Success",
        type: "success",
        pagename: pageName,
        status: response.data?.message || "Configuration updated successfully",
      });
    } catch (error) {
      setNotify({
        isOpen: true,
        message: error?.response?.data?.message || "Error",
        type: "error",
        pagename: pageName,
        status:
          error?.response?.data?.messageDetail ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
      setOpenFullConfigDialog(false);
    }
  };

  const onUpdateFirmware = (row) => {
    if (row.macAddress) {
      telemetry
        .get(`deviceManager/updateFirmware/${row.macAddress}`)
        .then((response) => {
          setMetaData(response.data.data);
          setOpenMetaForm(true);
          setNotify({
            isOpen: true,
            message: response.data.status,
            type: "info",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error.response.data.message,
            type: "info",
            pagename: pageName,
            status: error.response.messageDetail,
          });
        });
    }
  };

  const onReboot = (row) => {
    if (row.macAddress) {
      telemetry
        .get(`deviceManager/reboot/${row.macAddress}`)
        .then((response) => {
          setMetaData(response.data.data);
          setOpenMetaForm(true);
          setNotify({
            isOpen: true,
            message: response.data.status,
            type: "info",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error.response.data.message,
            type: "info",
            pagename: pageName,
            status: error.response.messageDetail,
          });
        });
    }
  };

  const onReset = (row) => {
    if (row.macAddress) {
      telemetry
        .get(`deviceManager/reset/${row.macAddress}`)
        .then((response) => {
          setMetaData(response.data.data);
          setOpenMetaForm(true);
          setNotify({
            isOpen: true,
            message: response.data.status,
            type: "info",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error.response.message,
            type: "info",
            pagename: pageName,
            status: error.response.messageDetail,
          });
        });
    }
  };

  const onDeployConfiguration = (row) => {
    if (row.macAddress) {
      telemetry
        .get(`deviceManager/updateConfiguration/${row.macAddress}`)
        .then((response) => {
          setMetaData(response.data.data);
          setOpenMetaForm(true);
          setNotify({
            isOpen: true,
            message: response.data.status,
            type: "info",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error.response.data.message,
            type: "info",
            pagename: pageName,
            status: error.response.messageDetail,
          });
        });
    }
  };

  const renderErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title={`${pageName}`}
      content={`${pageName} ${Attributes.messages.error}`}
      open={errorSB}
      onClose={closeErrorSB}
      close={closeErrorSB}
    />
  );

  const renderSuccessSB = (
    <MDSnackbar
      color="success"
      icon="check"
      title={`${pageName}`}
      content={`${pageName} ${Attributes.messages.success}`}
      open={successSB}
      onClose={closeSuccessSB}
      close={closeSuccessSB}
    />
  );

  return (
    <MDBox>
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
        }}
      >
        <Fade in={loading}>
          <div className={classes.loadingContainer}>
            <MDTypography variant="body1">
              <CircularProgress color="info" /> &nbsp; Loading...
            </MDTypography>
          </div>
        </Fade>
      </MDBox>

      <DeleteConfirmation
        entityName="Asset"
        data={selectedRow}
        columns={getColumns()}
        confirmDelete={confirmDelete}
        onAgree={onAgree}
        onCancel={onCancel}
      />

      <DeleteConfirmation
        entityName="Asset"
        data={selectedRow}
        columns={getColumns()}
        confirmDelete={confirmMultipleDelete}
        onAgree={onMultipleDeleteAgree}
        onCancel={onCancel}
      />

      <CTLBaseDialogForm
        entityName="Asset"
        columns={getColumns()}
        openForm={openForm}
        onSaveForm={onSaveForm}
        onCloseForm={onCloseForm}
        formData={formData}
        pageAttributes={Attributes}
        disableSaveButton={saving}
      />

      {metadata && metadata.form && (
        <CTLBaseDialogForm
          entityName="Device Configuration"
          columns={metadata.form.content}
          openForm={openMetaFrom}
          onSaveForm={onSaveMetaForm}
          onCloseForm={onCloseMetaForm}
          formData={deviceData || []}
          pageAttributes={{
            columns: 3,
            tabs: metadata?.form.tabs || [],
          }}
        />
      )}

      <CTLAdvancedFilter
        entityName="Asset"
        columns={getColumns()}
        applyFilter={openFilter}
        onApplyFilter={onApplyFilter}
        onCloseFilter={onCloseFilter}
      />

      <CTLTable
        apiURL={operationURLS.list}
        advancedFilters={advancedFilters}
        columns={getColumns(
          deleteRow,
          editRow,
          onUpdateConfiguration,
          onUpdateFirmware,
          onReboot
          // onReset,
          // onDeployConfiguration
        )}
        entriesPerPage={false}
        canSearch
        defaultSortDirection="asc"
        defaultSortColumn="id"
        pageId={pageId}
        list
        searchText={searchText}
        hasRowSelection={true}
        updateList={updateList}
        onSelectionChange={onSelectionChange}
      />
      {renderSuccessSB}
      {renderErrorSB}

      <UploadFileDialog
        open={openUploadFile}
        setOpen={setOpenUploadFile}
        onUpload={handleUploadFile}
        uploading={uploading}
      />

      <UploadSampleFileDialog
        open={openUploadSampleFile}
        setOpen={setOpenUploadSampleFile}
        onUpload={handleUploadSampleFile}
        uploading={uploadingSampleFile}
      />

      <FullConfigDialog
        open={openFullConfigDialog}
        onClose={() => setOpenFullConfigDialog(false)}
        onSubmit={onUpdateConfigurationSubmit}
        rowData={rowData}
      />

      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}
export default Asset;
